import Box from "@mui/material/Box";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGetStockAdjustmentService } from "subModule/src/services/inventory/useInventory";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import Loader from "layout/Loader";
import BackendPaginationTable from "common/Table/TableWithPagination";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import { useNavigate } from "react-router-dom";
import { SelectChangeEvent } from "@mui/material";
import { useGetStoreDropdownService } from "subModule/src/services/store/useStore";
import StockAdjustmentSearchHeader from "components/Inventory/StockAdjustmentSearchHeader";

interface SearchFormData {
  [key: string]: string | number;
}
export default function StockAdjustmentListing() {
  const navigate = useNavigate();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [filter, setFilter] = useState<string[]>(["itemCode"]);
  const [search, setSearch] = useState<any>({});
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    itemCode: "",
    storeId: 0,
    storeTitle: "",
  });
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetStockAdjustmentService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
  });
  const { data: storesData, refetch: storesRefetch } =
    useGetStoreDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  useEffect(() => {
    storesRefetch();
  }, []);
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "stores")
        storesRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const showRowView = (row: any) =>
    navigate("/inventory/stock-adjustment/" + row.id + "/print");
  const clearSearch = () => {
    setFilter(["itemCode"]);
    setSearchFormData({ itemCode: "", storeId: 0 });
    setSearch({});
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    const selectedFilters =
      typeof value === "string" ? value.split(",") : value;
    setFilter(selectedFilters);
    setSearchFormData((prevFormData) => {
      const updatedFormData = Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          selectedFilters.includes(key)
            ? prevFormData[key]
            : typeof prevFormData[key] === "string"
              ? ""
              : 0,
        ]),
      );
      if (selectedFilters.includes("storeId") && prevFormData.storeTitle)
        updatedFormData.storeTitle = prevFormData.storeTitle;
      return updatedFormData;
    });
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val =
      field === "storeId" ? (value ? parseInt(value.value) || 0 : 0) : value;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
      ...(field === "storeId" && value?.label
        ? { storeTitle: value.label }
        : {}),
    }));
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string | number> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
      }
    });
    setSearch(filteredSearch);
    setPageNo(1);
    setPageSize(pageSize);
  };

  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/inventory/stock-adjustment")}
        heading={t("StockAdjustments")}
        icon={
          <WidgetsOutlinedIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={checkPrivileges("ROLE_INVENTORYADJUSTMENT_CREATE")}
      />
      <Box>
        {isFetching ? (
          <Loader />
        ) : (
          <div id="mainListingTable">
            <BackendPaginationTable
              columns={[
                {
                  id: "itemCode",
                  label: t("itemCode"),
                  numeric: false,
                  width: "16%",
                },
                {
                  id: "storeTitle",
                  label: t("store"),
                  numeric: false,
                  width: "16%",
                },
                {
                  id: "quantityAdjusted",
                  label: "Quantity",
                  numeric: false,
                  width: "12%",
                },
                {
                  id: "stockAdjustmentType",
                  label: "adjustmentType",
                  numeric: false,
                  width: "16%",
                },
                {
                  id: "createdAt",
                  label: "createdAt",
                  numeric: false,
                  width: "22%",
                },
                {
                  id: "createdBy",
                  label: "createdBy",
                  numeric: false,
                  width: "18%",
                },
              ]}
              data={tableData?.pages[0]?.data?.content || []}
              pageNo={pageNo}
              pageSize={pageSize}
              tableData={tableData?.pages[0]?.data || {}}
              showCheckbox={false}
              showActions={true}
              enableSorting={true}
              showHeader={true}
              showSearch={true}
              showFilter={true}
              showCreateButton={true}
              handleChangePage={handleChangePage}
              handleChangeRows={handleChangeRows}
              rowClick={checkPrivileges("ROLE_INVENTORYADJUSTMENT_VIEW")}
              showRowView={showRowView}
              loadingSearch={false}
              headerComponent={
                <StockAdjustmentSearchHeader
                  searchFormData={searchFormData}
                  handleChangeFilter={handleChangeFilter}
                  handleChangeSearch={handleChangeSearch}
                  handleSubmitSearch={handleSubmitSearch}
                  clearSearch={clearSearch}
                  storesData={storesData?.pages[0]?.data?.content || []}
                  storesResponse={storesData || {}}
                  handleDropdownActions={handleDropdownActions}
                  {...{
                    filter,
                    search,
                    dropdownPage,
                    searchLoading,
                    dropdownSearchTerm,
                    setDropdownSearchTerm,
                    setDropdownPage,
                  }}
                />
              }
            />
          </div>
        )}
      </Box>
    </Box>
  );
}
